import { Field, Form, Formik } from 'formik'
import React, { ReactNode, useEffect, useState, useRef } from 'react'
import { InvoiceApi, EInvoiceModel, EInvoiceRequestModel, InvoiceConfigurationModel } from '../../services/InvoiceApi'
import { InlineDateEdit } from '../../shared/forms/InlineDateEdit'
import { OfferionErrorMessage } from '../../shared/forms/validation/OfferionErrorMessage'
import { Currency } from '../../shared/models'
import * as Yup from 'yup'
import { OfferionTooltip } from '../../shared/tooltip'
import { CurrencyLabel } from '../../shared/ui/currency'
import { useOfferionTranslation } from '../../shared/store/hooks/useOfferionTranslation'
import { Checkbox } from '../ui/Checkbox'
import { ResizeableTextarea } from '../../shared/forms/ResizeableTextarea'
import { toastWarning } from '../../shared/toastr'
import { isLawInEffect } from '../../shared/utils/dates'
import { date } from '../../shared/utils/formatters'

export enum EInvoiceModalType {
    SendEinvoice,
    DownloadEInvoice,
}

interface Props {
    type: EInvoiceModalType
    close: () => void
    details: {
        forCharge: number
        clientEmail: string
        documentNumber: string
        clientName: string
        invoiceId: number
        currency: Currency
        creator: string
        officialDate: Date | string
    }
    onSubmit: (data: EInvoiceRequestModel) => Promise<void>
    documentFooterRenderer?: () => ReactNode
    eInvoiceData: EInvoiceModel
    hasTaxFreeItems: boolean
}

export const EInvoiceModal = ({ type, details, close, onSubmit, documentFooterRenderer, eInvoiceData, hasTaxFreeItems }: Props) => {
    const { t } = useOfferionTranslation()
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const [configuration, setConfiguration] = useState<InvoiceConfigurationModel | null>(null)
    const [showDeliveryLocation, setShowDeliveryLocation] = useState(false)
    const [showAdditionalDocuments, setShowAdditionalDocuments] = useState(false)

    useEffect(() => {
        InvoiceApi.getConfiguration(String(details.invoiceId)).then(cfg => setConfiguration(cfg))
    }, [details.invoiceId])

    if (!configuration) {
        return null
    }

    const itemsWithoutClassificationCode = eInvoiceData.classificationCodeItems.filter(
        rc => !rc.classificationCode || !rc.classificationCode.trim()
    )
    const hasItemsWithoutClassificationCode = itemsWithoutClassificationCode.length > 0

    const isAccountingVat = configuration.isAccountingVat
    const hasExemptItemsOrFreeCompany = hasTaxFreeItems || isAccountingVat

    const EXEMPT_CATEGORIES = ['HR:E', 'HR:O', 'HR:AE', 'HR:POVNAK', 'HR:N', 'HR:G', 'HR:PP', 'HR:PPMV', 'HR:K']

    const validateGln = (gln: string | null | undefined) => {
        if (!gln) return true
        const glnRegex = /^\d{13}$/
        if (!glnRegex.test(gln)) return false

        let sum = 0
        for (let i = 0; i < 12; i++) {
            sum += parseInt(gln[i]) * (i % 2 === 0 ? 1 : 3)
        }
        const checkDigit = (10 - (sum % 10)) % 10
        return checkDigit === parseInt(gln[12])
    }

    const schema = Yup.object().shape({
        toEmail: Yup.string()
            .test('required-if-not-croatian', t('Allaround.Validation.required', { fieldName: t('Invoice.Send.email') }), val => {
                if (configuration.isCroatianCompany) return true
                return !!val && val.length > 0
            })
            .nullable(),
        orderReference: Yup.string()
            .when('processIdentificator', (processIdentificator: string, schema: Yup.StringSchema) => {
                const shouldBeRequired =
                    processIdentificator == 'P1' ||
                    processIdentificator == 'P3' ||
                    processIdentificator == 'P6' ||
                    processIdentificator == 'P7' ||
                    processIdentificator == 'P8'
                if (shouldBeRequired)
                    return schema.required(t('Allaround.Validation.required', { fieldName: t('Invoice.SendEInvoice.purchaseOrderNum') }))
                return schema.notRequired()
            })
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.purchaseOrderNum'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        contractReference: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.contractReference'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        projectReference: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.projectReference'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        receiptReference: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.receiptReference'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        despatchReference: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.despatchReference'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        salesOrderReference: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.salesOrderReference'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        originatorReference: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.originatorReference'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        invoicedItemId: Yup.string()
            .max(
                1024,
                t('Allaround.Validation.maxLength', {
                    fieldName: t('Invoice.SendEInvoice.invoicedItemId'),
                    maxLength: 1024,
                })
            )
            .nullable(),
        taxCategory: Yup.string()
            .when('isTaxExemptionReasonGlobal', (isGlobal: boolean, schema: Yup.StringSchema) => {
                if (!hasExemptItemsOrFreeCompany) {
                    return schema.notRequired()
                }
                if (isGlobal) {
                    return schema.required(t('Allaround.Validation.required', { fieldName: t('Invoice.SendEInvoice.taxCategory') }))
                }
                return schema.notRequired()
            })
            .nullable(),
        taxExemptionReason: Yup.string()
            .when('isTaxExemptionReasonGlobal', {
                is: true,
                then: Yup.string().test(
                    'required-if-any-exempt',
                    t('Allaround.Validation.required', { fieldName: t('Invoice.SendEInvoice.globalExemptionReason') }),
                    function (val) {
                        if (!hasExemptItemsOrFreeCompany) {
                            return true
                        }

                        const { reasonContainers } = this.parent
                        const hasExempt = reasonContainers?.some(rc => EXEMPT_CATEGORIES.includes(rc.taxCategory))
                        if (hasExempt) {
                            return !!val && val.length > 0
                        }
                        return true
                    }
                ),
                otherwise: Yup.string().notRequired(),
            })
            .nullable(),
        reasonContainers: Yup.array().when('isTaxExemptionReasonGlobal', {
            is: false,
            then: Yup.array().of(
                Yup.object().shape({
                    reason: Yup.string()
                        .when('taxCategory', {
                            is: (taxCategory: string) => EXEMPT_CATEGORIES.includes(taxCategory),
                            then: Yup.string().required(
                                t('Allaround.Validation.required', { fieldName: t('Invoice.SendEInvoice.itemExemptionReason') })
                            ),
                            otherwise: Yup.string().notRequired(),
                        })
                        .nullable(),
                    taxCategory: Yup.string()
                        .required(t('Allaround.Validation.required', { fieldName: t('Invoice.SendEInvoice.taxCategory') }))
                        .nullable(),
                })
            ),
        }),
        globalLocationNumber: Yup.string()
            .test('gln-check', t('Invoice.SendEInvoice.Validation.invalidGLN'), val => validateGln(val))
            .nullable(),
        streetName: Yup.string().max(100).nullable(),
        postalZone: Yup.string().max(10).nullable(),
        cityName: Yup.string().max(100).nullable(),
    })

    const defaultTaxCategory = isAccountingVat ? (configuration?.isCroatianCompany !== false ? 'HR:PDV' : 'S') : 'HR:E'

    const initialValues = {
        ...eInvoiceData,
        taxExemptionReason: eInvoiceData.taxExemptionReason,
        downloadAfterSend: false,
        type: type,
        invoiceId: details.invoiceId,
        toEmail: details.clientEmail,
        processIdentificator: (eInvoiceData.processIdentificator && eInvoiceData.processIdentificator.trim()) || 'P99:Oznaka kupca',
        deliveryDate: eInvoiceData.deliveryDate || new Date(),
        shouldFiscalize: false,
        isTaxExemptionReasonGlobal: true,
        attachments: eInvoiceData.attachments || [],
        taxCategory: defaultTaxCategory,
        reasonContainers: eInvoiceData.reasonContainers.map(container => ({
            ...container,
            reason: container.reason,
            taxCategory: container.taxCategory || eInvoiceData.taxCategory || defaultTaxCategory,
        })),
        useDefaultClassificationCode:
            isLawInEffect() && hasItemsWithoutClassificationCode && !!eInvoiceData.defaultClassificationCode?.trim(),
    }

    const fileToBase64 = (file: File): Promise<string> =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = error => reject(error)
        })

    return (
        <div className="popup__overlay popup__overlay--show send-document-by-email" style={{ cursor: 'default' }}>
            <div className="popup">
                <h1 className="overlay__title">
                    {' '}
                    {t(
                        type == EInvoiceModalType.DownloadEInvoice
                            ? 'Invoice.SendEInvoice.pageTitleDownload'
                            : 'Invoice.SendEInvoice.pageTitleSend'
                    )}
                    {type == EInvoiceModalType.DownloadEInvoice && (
                        <OfferionTooltip
                            placement="right"
                            titleKey="Invoice.SendEInvoice.pageTitleDownload.infoTitle"
                            bodyKey="Invoice.SendEInvoice.pageTitleDownload.infoText"
                        />
                    )}
                </h1>
                <div className="popup__close icono-cross" onClick={close} style={{ cursor: 'pointer' }}>
                    {t('Allaround.Popup.close')}
                </div>
                <div className="invoice-info-placeholder mod-margin-bottom-8">
                    {t('Invoice.Send.invoiceAttached')}
                    <span className="invoice-info">
                        <ul>
                            <li>{details.documentNumber}</li>
                            <li>{details.clientName}</li>
                            <li>
                                <CurrencyLabel amount={details.forCharge} currency={details.currency} />
                            </li>
                            <li>{details.officialDate ? date(details.officialDate) : null}</li>
                        </ul>
                    </span>
                </div>
                <Formik
                    enableReinitialize={false}
                    validationSchema={schema}
                    initialValues={initialValues}
                    onSubmit={(values, ctx) => {
                        const isAfterLawChange = isLawInEffect()
                        const isExemptProcess =
                            values.processIdentificator === 'P4' ||
                            values.processIdentificator === 'P9' ||
                            values.processIdentificator === 'P10'

                        if (
                            isAfterLawChange &&
                            hasItemsWithoutClassificationCode &&
                            !values.useDefaultClassificationCode &&
                            !isExemptProcess
                        ) {
                            const itemNames = itemsWithoutClassificationCode.map(rc => rc.name).join(', ')

                            const toastKey =
                                type === EInvoiceModalType.SendEinvoice
                                    ? 'Allaround.Validation.Fiscalization.eInvoiceWrongClassificationCodeIntro'
                                    : 'Allaround.Validation.Fiscalization.downloadWrongClassificationCodeIntro'

                            toastWarning(toastKey, undefined, undefined, {
                                itemNames,
                            })
                            return
                        }

                        onSubmit(values).catch(() => {
                            if (values.shouldFiscalize) ctx.setFieldValue('shouldFiscalize', false)
                        })
                    }}
                >
                    {formikProps => (
                        <Form noValidate={true}>
                            <div className="address-placeholder">
                                <div className="info__row">
                                    <label className="info__row-name" htmlFor="">
                                        {t('Invoice.Send.email')}
                                    </label>
                                    <div className="info__row-content mod-margin-8">
                                        <Field
                                            type="email"
                                            className="address-input"
                                            name="toEmail"
                                            placeholder={t('Invoice.Send.emailPlaceholder')}
                                        />
                                        <OfferionErrorMessage name="toEmail" />
                                    </div>
                                </div>
                                <div className="info__row">
                                    <label className="info__row-name" htmlFor="">
                                        {t('Invoice.SendEInvoice.deliveryDate')}
                                    </label>
                                    <div className="info__row-content mod-margin-bottom-8">
                                        <InlineDateEdit
                                            className="offerion-date"
                                            currentValue={formikProps.values.deliveryDate}
                                            onSave={({ newValue }) => formikProps.setFieldValue('deliveryDate', newValue)}
                                        />
                                        <OfferionErrorMessage name="deliveryDate" />
                                    </div>
                                </div>

                                <div className="info__row">
                                    <label className="info__row-name" htmlFor="">
                                        {t('Invoice.SendEInvoice.invoiceProcess')}
                                    </label>
                                    <div className="info__row-content mod-margin-bottom-8">
                                        <Field as="select" className="offerion-select" name="processIdentificator">
                                            <option value="P1">{t('Invoice.SendEInvoice.InvoiceProcess.p1')}</option>
                                            <option value="P2">{t('Invoice.SendEInvoice.InvoiceProcess.p2')}</option>
                                            <option value="P3">{t('Invoice.SendEInvoice.InvoiceProcess.p3')}</option>
                                            <option value="P4">{t('Invoice.SendEInvoice.InvoiceProcess.p4')}</option>
                                            <option value="P5">{t('Invoice.SendEInvoice.InvoiceProcess.p5')}</option>
                                            <option value="P6">{t('Invoice.SendEInvoice.InvoiceProcess.p6')}</option>
                                            <option value="P7">{t('Invoice.SendEInvoice.InvoiceProcess.p7')}</option>
                                            <option value="P8">{t('Invoice.SendEInvoice.InvoiceProcess.p8')}</option>
                                            <option value="P9">{t('Invoice.SendEInvoice.InvoiceProcess.p9')}</option>
                                            <option value="P10">{t('Invoice.SendEInvoice.InvoiceProcess.p10')}</option>
                                            <option value="P11">{t('Invoice.SendEInvoice.InvoiceProcess.p11')}</option>
                                            <option value="P12">{t('Invoice.SendEInvoice.InvoiceProcess.p12')}</option>
                                            <option value="P99:Oznaka kupca">{t('Invoice.SendEInvoice.InvoiceProcess.p99')}</option>
                                        </Field>
                                    </div>
                                </div>

                                <div className="info__row">
                                    <label className="info__row-name" htmlFor="">
                                        {t('Invoice.SendEInvoice.purchaseOrderNum')}
                                    </label>
                                    <div className="info__row-content mod-margin-bottom-8">
                                        <Field type="text" name="orderReference" />
                                        <OfferionErrorMessage name="orderReference" />
                                    </div>
                                </div>

                                <div
                                    className="info__row clickable"
                                    onClick={() => setShowDeliveryLocation(!showDeliveryLocation)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <h1 className="table__cell__highlighted-text">
                                        <span className="icon icon--clickable icon__info"></span>
                                        {t('Invoice.SendEInvoice.DeliveryLocation.title')}
                                    </h1>

                                    <button type="button" className="button button--gray right">
                                        <span className="button button--gray right">
                                            <span className={`icon ${showDeliveryLocation ? 'icon__up' : 'icon__down-blue'}`}></span>
                                        </span>
                                    </button>
                                </div>

                                {showDeliveryLocation && (
                                    <div className="mod-padding-left-16">
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="globalLocationNumber">
                                                {t('Invoice.SendEInvoice.DeliveryLocation.gln')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="globalLocationNumber"
                                                    className="address-input"
                                                    name="globalLocationNumber"
                                                    placeholder={t('Invoice.SendEInvoice.DeliveryLocation.glnPlaceholder')}
                                                />
                                                <OfferionErrorMessage name="globalLocationNumber" />
                                            </div>
                                        </div>

                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="streetName">
                                                {t('Invoice.SendEInvoice.DeliveryLocation.streetName')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="streetName"
                                                    className="address-input"
                                                    name="streetName"
                                                    placeholder={t('Invoice.SendEInvoice.DeliveryLocation.streetNamePlaceholder')}
                                                />
                                                <OfferionErrorMessage name="streetName" />
                                            </div>
                                        </div>

                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="postalZone">
                                                {t('Invoice.SendEInvoice.DeliveryLocation.postalZone')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="postalZone"
                                                    className="address-input"
                                                    name="postalZone"
                                                    placeholder={t('Invoice.SendEInvoice.DeliveryLocation.postalZonePlaceholder')}
                                                />
                                                <OfferionErrorMessage name="postalZone" />
                                            </div>
                                        </div>

                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="cityName">
                                                {t('Invoice.SendEInvoice.DeliveryLocation.cityName')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="cityName"
                                                    className="address-input"
                                                    name="cityName"
                                                    placeholder={t('Invoice.SendEInvoice.DeliveryLocation.cityNamePlaceholder')}
                                                />
                                                <OfferionErrorMessage name="cityName" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div
                                    className="info__row clickable"
                                    onClick={() => setShowAdditionalDocuments(!showAdditionalDocuments)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <h1 className="table__cell__highlighted-text">
                                        <span className="icon icon--clickable icon__info"></span>
                                        {t('Invoice.SendEInvoice.AdditionalDocuments.title')}
                                    </h1>

                                    <button type="button" className="button button--gray right">
                                        <span className="button button--gray right">
                                            <span className={`icon ${showAdditionalDocuments ? 'icon__up' : 'icon__down-blue'}`}></span>
                                        </span>
                                    </button>
                                </div>

                                {showAdditionalDocuments && (
                                    <div className="mod-padding-left-16">
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="contractReference">
                                                {t('Invoice.SendEInvoice.contractReference')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="contractReference"
                                                    className="address-input"
                                                    name="contractReference"
                                                />
                                                <OfferionErrorMessage name="contractReference" />
                                            </div>
                                        </div>
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="projectReference">
                                                {t('Invoice.SendEInvoice.projectReference')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="projectReference"
                                                    className="address-input"
                                                    name="projectReference"
                                                />
                                                <OfferionErrorMessage name="projectReference" />
                                            </div>
                                        </div>
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="receiptReference">
                                                {t('Invoice.SendEInvoice.receiptReference')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="receiptReference"
                                                    className="address-input"
                                                    name="receiptReference"
                                                />
                                                <OfferionErrorMessage name="receiptReference" />
                                            </div>
                                        </div>
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="despatchReference">
                                                {t('Invoice.SendEInvoice.despatchReference')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="despatchReference"
                                                    className="address-input"
                                                    name="despatchReference"
                                                />
                                                <OfferionErrorMessage name="despatchReference" />
                                            </div>
                                        </div>
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="salesOrderReference">
                                                {t('Invoice.SendEInvoice.salesOrderReference')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="salesOrderReference"
                                                    className="address-input"
                                                    name="salesOrderReference"
                                                />
                                                <OfferionErrorMessage name="salesOrderReference" />
                                            </div>
                                        </div>
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="originatorReference">
                                                {t('Invoice.SendEInvoice.originatorReference')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field
                                                    type="text"
                                                    id="originatorReference"
                                                    className="address-input"
                                                    name="originatorReference"
                                                />
                                                <OfferionErrorMessage name="originatorReference" />
                                            </div>
                                        </div>
                                        <div className="info__row">
                                            <label className="info__row-name" htmlFor="invoicedItemId">
                                                {t('Invoice.SendEInvoice.invoicedItemId')}
                                            </label>
                                            <div className="info__row-content mod-margin-bottom-8">
                                                <Field type="text" id="invoicedItemId" className="address-input" name="invoicedItemId" />
                                                <OfferionErrorMessage name="invoicedItemId" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {hasTaxFreeItems && formikProps.values.isTaxExemptionReasonGlobal && (
                                    <div className="info__row">
                                        <label className="info__row-name" htmlFor="">
                                            {t('Invoice.SendEInvoice.taxCategory')}
                                        </label>
                                        <div className="info__row-content mod-margin-bottom-8">
                                            <Field
                                                as="select"
                                                className="offerion-select"
                                                name="taxCategory"
                                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                    const newValue = e.target.value
                                                    formikProps.setFieldValue('taxCategory', newValue)

                                                    if (formikProps.values.isTaxExemptionReasonGlobal) {
                                                        const updatedContainers = formikProps.values.reasonContainers.map(container => ({
                                                            ...container,
                                                            taxCategory: newValue,
                                                        }))
                                                        formikProps.setFieldValue('reasonContainers', updatedContainers)
                                                    }
                                                }}
                                            >
                                                {configuration?.isCroatianCompany !== false ? (
                                                    <>
                                                        <option value="HR:K">{t('Invoice.SendEInvoice.TaxCategory.HR_K')}</option>
                                                        <option value="HR:G">{t('Invoice.SendEInvoice.TaxCategory.HR_G')}</option>
                                                        <option value="HR:AE">{t('Invoice.SendEInvoice.TaxCategory.HR_AE')}</option>
                                                        <option value="HR:E">{t('Invoice.SendEInvoice.TaxCategory.HR_E')}</option>
                                                        <option value="HR:POVNAK">{t('Invoice.SendEInvoice.TaxCategory.HR_POVNAK')}</option>
                                                        <option value="HR:PP">{t('Invoice.SendEInvoice.TaxCategory.HR_PP')}</option>
                                                        <option value="HR:PPMV">{t('Invoice.SendEInvoice.TaxCategory.HR_PPMV')}</option>
                                                        {eInvoiceData.reasonContainers &&
                                                            eInvoiceData.classificationCodeItems.length > 0 &&
                                                            eInvoiceData.classificationCodeItems.every(rc => rc.isTaxFree) && (
                                                                <option value="HR:O">{t('Invoice.SendEInvoice.TaxCategory.HR_O')}</option>
                                                            )}
                                                        <option value="HR:N">{t('Invoice.SendEInvoice.TaxCategory.HR_N')}</option>
                                                    </>
                                                ) : (
                                                    <>
                                                        <option value="HR:K">{t('Invoice.SendEInvoice.TaxCategory.EU_K')}</option>
                                                        <option value="HR:G">{t('Invoice.SendEInvoice.TaxCategory.EU_G')}</option>
                                                        <option value="HR:AE">{t('Invoice.SendEInvoice.TaxCategory.EU_AE')}</option>
                                                        {eInvoiceData.reasonContainers &&
                                                            eInvoiceData.classificationCodeItems.length > 0 &&
                                                            eInvoiceData.classificationCodeItems.every(rc => rc.isTaxFree) && (
                                                                <option value="HR:O">{t('Invoice.SendEInvoice.TaxCategory.EU_O')}</option>
                                                            )}
                                                        <option value="HR:E">{t('Invoice.SendEInvoice.TaxCategory.EU_E')}</option>
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                )}
                                {hasExemptItemsOrFreeCompany && formikProps.values.reasonContainers.length ? (
                                    <>
                                        {formikProps.values.isTaxExemptionReasonGlobal ? (
                                            <div>
                                                <div className="info__row">
                                                    <label className="info__row-name" htmlFor="">
                                                        {t('Invoice.SendEInvoice.globalExemptionReason')}
                                                    </label>
                                                    <div className="info__row-content mod-margin-8">
                                                        <Field
                                                            type="text"
                                                            className="address-input"
                                                            name="taxExemptionReason"
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const newValue = e.target.value
                                                                formikProps.setFieldValue('taxExemptionReason', newValue)

                                                                if (formikProps.values.isTaxExemptionReasonGlobal) {
                                                                    const updatedContainers = formikProps.values.reasonContainers.map(
                                                                        container => ({
                                                                            ...container,
                                                                            reason: newValue,
                                                                        })
                                                                    )
                                                                    formikProps.setFieldValue('reasonContainers', updatedContainers)
                                                                }
                                                            }}
                                                        />
                                                        <OfferionErrorMessage name="taxExemptionReason" />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <></>
                                        )}

                                        {formikProps.values.reasonContainers.length > 1 ? (
                                            <>
                                                <div className="info__row flex">
                                                    <Checkbox name="isTaxExemptionReasonGlobal" />
                                                    <div className="ml1">
                                                        <label htmlFor="isTaxExemptionReasonGlobal">
                                                            {t('Invoice.SendEInvoice.isTaxExemptionReasonGlobal')}
                                                        </label>
                                                    </div>
                                                </div>

                                                {!formikProps.values.isTaxExemptionReasonGlobal &&
                                                    formikProps.values.reasonContainers.map((item, index) => {
                                                        return (
                                                            <div key={index}>
                                                                <div className="info__row">
                                                                    <div className="mod-margin-bottom-8">
                                                                        <label className="info__row-name">
                                                                            {t('Invoice.SendEInvoice.taxCategoryFor', {
                                                                                itemName: item.name,
                                                                            })}
                                                                        </label>
                                                                        <Field
                                                                            as="select"
                                                                            className="offerion-select"
                                                                            name={`reasonContainers.${index}.taxCategory`}
                                                                        >
                                                                            {configuration?.isCroatianCompany !== false ? (
                                                                                <>
                                                                                    <option value="HR:K">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_K')}
                                                                                    </option>
                                                                                    <option value="HR:G">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_G')}
                                                                                    </option>
                                                                                    <option value="HR:AE">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_AE')}
                                                                                    </option>
                                                                                    <option value="HR:E">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_E')}
                                                                                    </option>
                                                                                    <option value="HR:POVNAK">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_POVNAK')}
                                                                                    </option>
                                                                                    <option value="HR:PP">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_PP')}
                                                                                    </option>
                                                                                    <option value="HR:PPMV">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_PPMV')}
                                                                                    </option>
                                                                                    {eInvoiceData.reasonContainers &&
                                                                                        eInvoiceData.classificationCodeItems.length > 0 &&
                                                                                        eInvoiceData.classificationCodeItems.every(
                                                                                            rc => rc.isTaxFree
                                                                                        ) && (
                                                                                            <option value="HR:O">
                                                                                                {t('Invoice.SendEInvoice.TaxCategory.HR_O')}
                                                                                            </option>
                                                                                        )}
                                                                                    <option value="HR:N">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.HR_N')}
                                                                                    </option>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <option value="HR:K">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.EU_K')}
                                                                                    </option>
                                                                                    <option value="HR:G">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.EU_G')}
                                                                                    </option>
                                                                                    <option value="HR:AE">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.EU_AE')}
                                                                                    </option>
                                                                                    {eInvoiceData.reasonContainers &&
                                                                                        eInvoiceData.classificationCodeItems.length > 0 &&
                                                                                        eInvoiceData.classificationCodeItems.every(
                                                                                            rc => rc.isTaxFree
                                                                                        ) && (
                                                                                            <option value="HR:O">
                                                                                                {t('Invoice.SendEInvoice.TaxCategory.EU_O')}
                                                                                            </option>
                                                                                        )}
                                                                                    <option value="HR:E">
                                                                                        {t('Invoice.SendEInvoice.TaxCategory.EU_E')}
                                                                                    </option>
                                                                                </>
                                                                            )}
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className="info__row">
                                                                    <label className="info__row-name" htmlFor="">
                                                                        {t('Invoice.SendEInvoice.exemptionReasonFor', {
                                                                            itemName: item.name,
                                                                        })}
                                                                    </label>
                                                                    <div className="info__row-content mod-margin-8">
                                                                        <Field
                                                                            component={ResizeableTextarea}
                                                                            className="address-input mod-margin-top-8"
                                                                            name={`reasonContainers.${index}.reason`}
                                                                        />
                                                                        <OfferionErrorMessage name={`reasonContainers.${index}.reason`} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                            </>
                                        ) : null}
                                    </>
                                ) : null}
                                {type == EInvoiceModalType.SendEinvoice && (
                                    <div className="info__row flex">
                                        <Checkbox name="downloadAfterSend" />
                                        <div className="ml1">
                                            <label htmlFor="downloadAfterSend">{t('Invoice.SendEInvoice.downloadAfterSendYesNo')}</label>
                                        </div>
                                    </div>
                                )}
                                {documentFooterRenderer && documentFooterRenderer()}
                                {isLawInEffect() &&
                                    hasItemsWithoutClassificationCode &&
                                    !!eInvoiceData.defaultClassificationCode?.trim() && (
                                        <div className="info__row flex">
                                            <Checkbox name="useDefaultClassificationCode" />
                                            <div className="ml1">
                                                <label htmlFor="useDefaultClassificationCode">
                                                    {t('Invoice.SendEInvoice.useDefaultClassificationCode')}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                            </div>

                            <div className="info__row">
                                <label className="info__row-name">{t('Invoice.SendEInvoice.attachments')}</label>
                                <div className="info__row-content mod-margin-8">
                                    <div className="mod-margin-bottom-16">
                                        <button
                                            type="button"
                                            style={{
                                                padding: '4px 8px',
                                                minHeight: '28px',
                                                height: '28px',
                                                lineHeight: '1 !important',
                                                fontSize: '13px !important',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            className="button button--gray popup__button"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <span
                                                className="icon icon__upload"
                                                style={{ transform: 'scale(0.7)', transformOrigin: 'center' }}
                                            ></span>
                                            {t('Invoice.SendEInvoice.chooseFiles')}
                                        </button>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        id="fileUploadInput"
                                        type="file"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={async e => {
                                            const files = e.target.files
                                            if (!files) return

                                            const newAttachments: { fileName: string; base64Content: string }[] = []

                                            for (const file of Array.from(files)) {
                                                const base64 = await fileToBase64(file)
                                                newAttachments.push({
                                                    fileName: file.name,
                                                    base64Content: base64.split(',')[1],
                                                })
                                            }

                                            formikProps.setFieldValue('attachments', [
                                                ...(formikProps.values.attachments || []),
                                                ...newAttachments,
                                            ])
                                        }}
                                    />

                                    {formikProps.values.attachments && formikProps.values.attachments.length > 0 && (
                                        <ul style={{ marginTop: 4, listStyle: 'none', padding: 0 }}>
                                            {formikProps.values.attachments.map((att, idx) => (
                                                <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                                    <span style={{ marginRight: 8 }}>{att.fileName}</span>
                                                    <span
                                                        className="icon icon--small icon__cross-red"
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                        onClick={() => {
                                                            const updated = [...formikProps.values.attachments]
                                                            updated.splice(idx, 1)
                                                            formikProps.setFieldValue('attachments', updated)
                                                        }}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className="popup__buttons mod-margin-top-8">
                                <button className="button button--gray popup__button" type="button" onClick={close}>
                                    <span className="icon icon--small icon__cross-red"></span>
                                    {t('Allaround.Button.cancel')}
                                </button>
                                <button className="button button--gray popup__button" type="submit">
                                    <span className="icon icon__check-green"></span>
                                    {t(type == EInvoiceModalType.SendEinvoice ? 'Allaround.Button.send' : 'Allaround.Button.download')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    )
}
